import {
    AggregatePaginateResult,
    ApprovalScopeEnum,
    AutoMatchPurchaseOrdersRequest,
    AutoMatchPurchaseOrdersResponse,
    CommentItem,
    PurchaseOrderDTO,
} from "@finway-group/shared/lib/models"
import { PurchaseOrderApprovalProcessDTO } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderDTO"
import { PurchaseOrderActions } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatusAction.enum"
import { QueryParamsObject, type TypeHints } from "@finway-group/shared/lib/models/queryParams/queryParams.types"
import axios from "axios"
import { QueryParams } from "~Shared/queries/consts"
import { PurchaseOrder, PurchaseOrderForLinking, PurchaseOrderLinks } from "../types/PurchaseOrder.interfaces"

export interface PurchaseOrderTransitionRequest {
    action: string
    reason?: string
}

const MODEL = "purchase-orders"

const poTypeHints: TypeHints = {
    purchaseOrderNumber: "string",
    description: "string",
}

export const PurchaseOrderApiService = {
    fetchPurchaseOrders: async ({
        params,
        signal,
    }: {
        params: QueryParamsObject & { forLinking?: boolean }
        signal?: AbortSignal
    }): Promise<AggregatePaginateResult<PurchaseOrder>> => {
        const requestParams = {
            ...params,
            typeHint: poTypeHints,
        }

        const { data } = await axios.get<AggregatePaginateResult<PurchaseOrder>>(`/${MODEL}`, { params: requestParams, signal })
        return data
    },
    fetchPurchaseOrdersForLinking: ({ params, signal }: { params: QueryParams; signal?: AbortSignal }): Promise<AggregatePaginateResult<PurchaseOrderForLinking>> => {
        const { sortParams, ...rest } = params
        return PurchaseOrderApiService.fetchPurchaseOrders({ params: { ...rest, forLinking: true }, signal })
    },
    getPurchaseOrderById: async (purchaseOrderId: string) => {
        const { data } = await axios.get(`/${MODEL}/${purchaseOrderId}`)
        return data
    },

    createAndSubmitPurchaseOrder: async (purchaseOrder: PurchaseOrderDTO) => {
        const { data } = await axios.post(`/${MODEL}`, purchaseOrder)
        return PurchaseOrderApiService.transitionPurchaseOrder(data._id, { action: PurchaseOrderActions.SUBMIT_FOR_APPROVAL })
    },
    createPurchaseOrder: async (purchaseOrder: PurchaseOrderDTO) => {
        const { data } = await axios.post(`/${MODEL}`, purchaseOrder)
        return data
    },
    updatePurchaseOrder: async (id: string, purchaseOrder: PurchaseOrderDTO) => {
        const { data } = await axios.put(`/${MODEL}/${id}`, purchaseOrder)
        return data
    },
    deletePurchaseOrder: async (purchaseOrderId: string) => {
        const { data } = await axios.delete(`/${MODEL}/${purchaseOrderId}`)
        return data
    },

    transitionPurchaseOrder: async (purchaseOrderId: string, transition: PurchaseOrderTransitionRequest) => {
        const { data } = await axios.patch(`/${MODEL}/${purchaseOrderId}/transition`, transition)
        return data
    },

    updateComments: async (purchaseOrderId: string, comments: Array<CommentItem>) => {
        const { data } = await axios.put(`/${MODEL}/${purchaseOrderId}/comments`, { comments })
        return data
    },

    attachTagToPurchaseOrder: async (purchaseOrderId: string, tagId: string) => {
        const { data } = await axios.post(`/${MODEL}/${purchaseOrderId}/tags/${tagId}`)
        return data
    },
    detachTagFromPurchaseOrder: async (purchaseOrderId: string, tagId: string) => {
        const { data } = await axios.delete(`/${MODEL}/${purchaseOrderId}/tags/${tagId}`)
        return data
    },

    getPurchaseOrderLinks: async (purchaseOrderId: string) => {
        const { data } = await axios.get<PurchaseOrderLinks>(`/${MODEL}/${purchaseOrderId}/links`)
        return data
    },

    autoMatchInvoiceItems: async (request: AutoMatchPurchaseOrdersRequest): Promise<AutoMatchPurchaseOrdersResponse> => {
        const { data } = await axios.post<AutoMatchPurchaseOrdersResponse>(`/${MODEL}/auto-match-invoice-items`, request)
        return data
    },
    buildApprovalProcesses: async (approvalProcessScopes: Array<ApprovalScopeEnum>, purchaseOrderData: PurchaseOrderApprovalProcessDTO) => {
        const { data } = await axios.post(`/${MODEL}/approval-processes`, {
            approvalProcessScopes,
            purchaseOrderData,
        })

        return data
    },
}
